<template>
    <div class="pl-page">
        <!-- Header -->
        <div class="pl-header">
            <div class="pl-header-left">
                <div class="pl-icon-wrap"><i class="fas fa-tags"></i></div>
                <h2 class="pl-title">Project Labels</h2>
                <span class="pl-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECT_LABELS">
                <button class="pl-add-btn" @click="showForm = true">
                    <i class="fas fa-plus"></i>
                    Add Label
                </button>
            </AllowPermission>
        </div>

        <!-- Form Modal -->
        <RightSideModal :show="showForm" :title="editMode ? 'Edit Label' : 'New Label'" @close="closeForm">
            <Form :fields="createFields" title="Project Label" submitUrl="/project-labels"
                updateRootUrl="/project-labels" @success="handleFormSuccess" @cancel="closeForm"
                :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="pl-loading"><div class="pl-spinner"></div><span>Loading labels...</span></div>

        <!-- Empty State -->
        <div v-else-if="!items.length" class="pl-empty">
            <div class="pl-empty-icon"><i class="fas fa-tags"></i></div>
            <h3>No project labels found</h3>
            <p>Create labels to organize your projects</p>
        </div>

        <!-- Labels Grid -->
        <div v-else class="pl-grid">
            <div class="pl-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="pl-card-header">
                    <div class="pl-label-badge" :style="{ background: item.color || labelColors[idx % labelColors.length] }">
                        <i class="fas fa-tag"></i>
                    </div>
                    <div class="pl-card-info">
                        <h4 class="pl-card-name">{{ item.name }}</h4>
                    </div>
                </div>
                <div class="pl-color-preview" v-if="item.color" :style="{ background: item.color }"></div>
                <div class="pl-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECT_LABELS">
                        <button class="pl-action-btn pl-btn-edit" @click="enableEditing(item)" title="Edit">
                            <i class="fas fa-pen"></i> Edit
                        </button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECT_LABELS">
                        <button class="pl-action-btn pl-btn-delete" @click="deleteItem(item.id)" title="Delete">
                            <i class="fas fa-trash-alt"></i> Delete
                        </button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { createField } from "../../utils/common"
import { http } from "../../utils/http"

export default {
    data() {
        return {
            PERMISSION_CODES,
            createFields: [
                createField("Name*", "name", "text", true),
                createField("Color", "color", "color", false),
            ],
            items: [],
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
            labelColors: ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#ef4444", "#6366f1"],
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    mounted() { this.fetchData() },
    methods: {
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async fetchData() {
            this.loading = true
            try { const res = await http.get("/project-labels"); this.items = res.data.result || [] } catch (err) { console.log(err) }
            this.loading = false
        },
        async deleteItem(id) {
            if (!confirm("Delete this label?")) return
            try { await http.delete(`/project-labels/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch (e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.pl-page { animation: plFade .3s ease; }
@keyframes plFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.pl-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}

.pl-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pl-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.pl-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.pl-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}

.pl-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover {
        background: $primaryDark;
    }
}

/* Loading / Empty */
.pl-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span {
        font-size: 13px;
        color: $gray400;
    }
}

.pl-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: plSpin .7s linear infinite;
}

@keyframes plSpin { to { transform: rotate(360deg); } }

.pl-empty {
    text-align: center;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 {
        font-size: 14px;
        font-weight: 700;
        color: $gray600;
        margin: 0 0 4px;
    }

    p {
        font-size: 12px;
        color: $gray400;
        margin: 0;
    }
}

.pl-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i {
        font-size: 20px;
        color: $gray300;
    }
}

/* Grid */
.pl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
}

.pl-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 14px;
    box-shadow: $shadowSm;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: plSlide .25s ease both;
    transition: all $transitionFast;

    &:hover {
        border-color: $gray200;
        box-shadow: $shadowMd;
        transform: translateY(-2px);
    }
}

@keyframes plSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

.pl-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pl-label-badge {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: white;
    flex-shrink: 0;
}

.pl-card-info {
    flex: 1;
    min-width: 0;
}

.pl-card-name {
    font-size: 14px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    line-height: 1.3;
}

/* Color Preview */
.pl-color-preview {
    width: 100%;
    height: 24px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Actions */
.pl-card-actions {
    display: flex;
    gap: 6px;
    padding-top: 8px;
    border-top: 1px solid $gray100;
}

.pl-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: 1px solid $gray200;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    background: white;
    flex: 1;
    justify-content: center;

    i {
        font-size: 10px;
    }
}

.pl-btn-edit {
    color: $primaryColor;

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.15);
    }
}

.pl-btn-delete {
    color: $danger;

    &:hover {
        background: #fef2f2;
        border-color: rgba(220, 38, 38, 0.15);
    }
}

/* Responsive */
@media (max-width: 992px) {
    .pl-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .pl-grid {
        grid-template-columns: 1fr;
    }

    .pl-card {
        padding: 12px;
    }

    .pl-header {
        flex-direction: column;
        align-items: stretch;
    }
}
</style>
